import { useState } from 'react'
import { Card, Table, Button, Tag, Space, Progress, Tabs, Form, Input, Select, Slider, Row, Col, Statistic, Alert, message, InputNumber } from 'antd'
import { PlusOutlined, PlayCircleOutlined, PauseCircleOutlined, EyeOutlined, DownloadOutlined, RocketOutlined, ExperimentOutlined } from '@ant-design/icons'
import type { FinetuneTask } from '../../types/admin'
import { mockFinetuneTasks } from '../../services/mockData'

const { TabPane } = Tabs
const { Option } = Select
const { TextArea } = Input

const ModelFinetune = () => {
  const [tasks, setTasks] = useState<FinetuneTask[]>(mockFinetuneTasks)
  const [activeTab, setActiveTab] = useState('tasks')
  const [form] = Form.useForm()

  const getStatusTag = (status: string) => {
    const config: Record<string, { color: string; text: string }> = {
      pending: { color: 'default', text: '等待中' },
      queued: { color: 'processing', text: '排队中' },
      running: { color: 'blue', text: '训练中' },
      paused: { color: 'warning', text: '已暂停' },
      completed: { color: 'success', text: '完成' },
      failed: { color: 'error', text: '失败' },
    }
    return <Tag color={config[status]?.color}>{config[status]?.text}</Tag>
  }

  const taskColumns = [
    {
      title: '任务名称',
      dataIndex: 'taskName',
      key: 'taskName',
    },
    {
      title: '基础模型',
      dataIndex: 'baseModel',
      key: 'baseModel',
      render: (text: string) => <Tag>{text}</Tag>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => getStatusTag(status),
    },
    {
      title: '进度',
      key: 'progress',
      render: (_: any, record: FinetuneTask) => (
        record.status === 'running' ? (
          <Progress percent={record.progress.percentage} />
        ) : record.status === 'completed' ? (
          <Progress percent={100} status="success" />
        ) : (
          <span>--</span>
        )
      ),
    },
    {
      title: '指标',
      key: 'metrics',
      render: (_: any, record: FinetuneTask) => (
        <span>Loss: {record.metrics.trainLoss.toFixed(3)}</span>
      ),
    },
    {
      title: '时间',
      key: 'time',
      render: (_: any, record: FinetuneTask) => (
        record.status === 'running' ? (
          <span>预计剩余 {Math.floor((record.estimatedTime || 0) / 60)}分钟</span>
        ) : record.status === 'completed' ? (
          <span>已完成</span>
        ) : (
          <span>--</span>
        )
      ),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: FinetuneTask) => (
        <Space>
          <Button type="link" icon={<EyeOutlined />}>详情</Button>
          {record.status === 'running' && (
            <Button type="link" icon={<PauseCircleOutlined />}>暂停</Button>
          )}
          {record.status === 'completed' && (
            <Button type="primary" icon={<RocketOutlined />}>部署</Button>
          )}
          <Button type="link" danger>删除</Button>
        </Space>
      ),
    },
  ]

  const handleCreateTask = () => {
    message.success('训练任务已创建')
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">🤖 在线模型微调</h1>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="训练任务" key="tasks">
          <Card
            title="📋 微调任务列表"
            extra={
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setActiveTab('create')}>
                新建训练任务
              </Button>
            }
          >
            <Table
              columns={taskColumns}
              dataSource={tasks}
              rowKey="taskId"
              pagination={{ pageSize: 10 }}
            />
          </Card>
        </TabPane>

        <TabPane tab="新建任务" key="create">
          <Card title="➕ 新建微调任务">
            <Form form={form} layout="vertical">
              <Form.Item
                name="taskName"
                label="任务名称"
                rules={[{ required: true }]}
              >
                <Input placeholder="例如：v1.3-技术参数-001" />
              </Form.Item>

              <Form.Item
                name="baseModel"
                label="基础模型"
                rules={[{ required: true }]}
                initialValue="qwen-14b"
              >
                <Select>
                  <Option value="qwen-14b">Qwen-14B-Chat (推荐)</Option>
                  <Option value="qwen-7b">Qwen-7B-Chat</Option>
                  <Option value="qwen-72b">Qwen-72B-Chat</Option>
                  <Option value="baichuan2-13b">Baichuan2-13B</Option>
                  <Option value="chatglm3-6b">ChatGLM3-6B</Option>
                </Select>
              </Form.Item>

              <Form.Item label="训练数据">
                <Alert
                  message="数据来源: 平台标注数据"
                  description="将使用已标注的高质量数据进行训练"
                  type="info"
                  showIcon
                  className="mb-4"
                />
                <div className="space-y-2">
                  <div>
                    <span className="mr-4">数据筛选:</span>
                    <Select mode="multiple" placeholder="选择质疑类型" style={{ width: 300 }}>
                      <Option value="资格条件">资格条件</Option>
                      <Option value="技术参数">技术参数</Option>
                      <Option value="报价评审">报价评审</Option>
                      <Option value="程序流程">程序流程</Option>
                    </Select>
                  </div>
                  <div>
                    <span className="mr-4">质量等级:</span>
                    <Select defaultValue="excellent" style={{ width: 150 }}>
                      <Option value="excellent">优秀</Option>
                      <Option value="good">良好</Option>
                      <Option value="qualified">合格</Option>
                    </Select>
                  </div>
                </div>
              </Form.Item>

              <Form.Item label="训练方法" name="method" initialValue="lora">
                <Select>
                  <Option value="lora">LoRA</Option>
                  <Option value="qlora">QLoRA</Option>
                  <Option value="full">全参数微调</Option>
                  <Option value="prefix">Prefix Tuning</Option>
                </Select>
              </Form.Item>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item label="LoRA Rank" name={['loraConfig', 'r']} initialValue={64}>
                    <Slider min={8} max={256} marks={{ 8: '8', 64: '64', 128: '128', 256: '256' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="LoRA Alpha" name={['loraConfig', 'alpha']} initialValue={128}>
                    <Slider min={16} max={512} marks={{ 16: '16', 128: '128', 256: '256', 512: '512' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Dropout" name={['loraConfig', 'dropout']} initialValue={0.05}>
                    <Slider min={0} max={0.2} step={0.01} marks={{ 0: '0', 0.05: '0.05', 0.1: '0.1', 0.2: '0.2' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={6}>
                  <Form.Item label="Epochs" name={['trainingArgs', 'epochs']} initialValue={3}>
                    <InputNumber min={1} max={10} className="w-full" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Batch Size" name={['trainingArgs', 'batchSize']} initialValue={4}>
                    <InputNumber min={1} max={16} className="w-full" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Learning Rate" name={['trainingArgs', 'learningRate']} initialValue={0.0002}>
                    <InputNumber min={0.00001} max={0.001} step={0.00001} className="w-full" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Max Seq Length" name={['trainingArgs', 'maxSeqLength']} initialValue={2048}>
                    <InputNumber min={512} max={4096} step={512} className="w-full" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item label="计算资源">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span>GPU: 自动分配 (检测到: 8x A100 80GB)</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span>DeepSpeed:</span>
                    <Select defaultValue="zero2" style={{ width: 150 }}>
                      <Option value="zero2">ZeRO-2</Option>
                      <Option value="zero3">ZeRO-3</Option>
                    </Select>
                  </div>
                  <div className="mt-2 text-sm text-gray-500">
                    预计训练时间: 2小时30分钟 | 预计显存占用: 56GB/GPU
                  </div>
                </div>
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button type="primary" icon={<PlayCircleOutlined />} onClick={handleCreateTask}>
                    开始训练
                  </Button>
                  <Button>保存为模板</Button>
                  <Button>保存草稿</Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>
        </TabPane>

        <TabPane tab="模型仓库" key="models">
          <Row gutter={16} className="mb-6">
            <Col span={6}>
              <Card>
                <Statistic title="模型总数" value={5} />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic title="已部署" value={2} />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic title="训练中" value={1} />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic title="总大小" value="156" suffix="GB" />
              </Card>
            </Col>
          </Row>

          <Card title="🏆 模型列表">
            <Table
              dataSource={mockFinetuneTasks.filter(t => t.status === 'completed')}
              rowKey="taskId"
              columns={[
                { title: '模型版本', dataIndex: 'taskName', key: 'taskName' },
                { title: '基础模型', dataIndex: 'baseModel', key: 'baseModel' },
                { title: '训练数据', key: 'data', render: () => '856 QA对' },
                { title: 'Loss', key: 'loss', render: (_: any, record: FinetuneTask) => record.metrics.trainLoss },
                { title: '状态', key: 'status', render: () => <Tag color="success">可用</Tag> },
                { 
                  title: '操作', 
                  key: 'action',
                  render: () => (
                    <Space>
                      <Button type="primary" icon={<RocketOutlined />}>部署</Button>
                      <Button icon={<DownloadOutlined />}>下载</Button>
                      <Button icon={<ExperimentOutlined />}>测试</Button>
                    </Space>
                  )
                },
              ]}
            />
          </Card>
        </TabPane>
      </Tabs>
    </div>
  )
}

export default ModelFinetune
